<template>
  <div
    :class="{
      'full-screen': isFullScreen,
      'embedded': !isFullScreen && !simpleView,
      'system-report': simpleView,
      'force-flex-column': isFullScreen
    }"
  >
    <div :class="{ 'column': isFullScreen }">
      <div :class="{ 'force-flex-grow': isFullScreen }">
        <q-table
          ref="table"
          :key="table.sortBy + '-' + table.descending"
          v-model:pagination="pagination"
          class="report-table"
          :square="table.finalCompactTable"
          :bordered="!table.finalCompactTable"
          :class="{
            'repeat-headers': repeatHeaders,
            'compact-table': table.finalCompactTable,
            'plain-table': table.finalPlainTableContent,
            'bg-transparent': table.finalCompactTable,
            'has-horizontal-overflow': hasHorizontalOverflow,
            'has-header-banner': hasHeaderBanner,
            'has-custom-description': hasCustomDescription,
            'printing': printing
          }"
          :rows="finalRows"
          :filter="filter"
          :row-key="rowKey"
          flat
          dense
          :sort-method="customSort"
          :loading="table.loading"
          :columns="processedColumns"
          :hide-bottom="processedRows && !!processedRows.length"
          :no-data-label="noDataLabel"
          wrap-cells
          :data-printable="readyToPrint ? 'ready' : 'not-ready'"
          @row-click="(event, row) => handleRowClick(event, row)"
        >
          <template #top>
            <div
              class="col items-center justify-between"
            >
              <div
                class="row"
              >
                <report-migratable-button
                  v-if="!printing && !editing && reportElement.migratable"
                  :class="{ 'data-freshness': reportStatus.done }"
                  size="sm"
                  @edit="$emit('edit', true)"
                />
                <report-status
                  v-if="!printing && !reportStatus.noData"
                  class="col-auto"
                  :class="{ 'data-freshness': reportStatus.done }"
                  size="sm"
                  :report-status="reportStatus"
                />
                <div
                  :class="table.finalCompactTable ? 'text-subtitle1' : 'q-table__title'"
                  class="col items-center justify-between"
                >
                  {{ reportOptions.has_custom_title ? reportOptions.custom_title : table.title }}
                  <q-btn
                    v-if="!printing && (reportElement.componentName === 'Table' || reportElement.componentName === 'Single Data Table')"
                    class="col-auto help-icon"
                    icon="help"
                    size="sm"
                    dense
                    round
                    @click="showInformationDialog = true"
                  >
                    <q-tooltip>
                      View Report Element Information
                    </q-tooltip>
                  </q-btn>
                </div>

                <q-btn
                  v-if="!printing && !showSearchField"
                  class="col-auto q-mr-sm search-button"
                  icon="search"
                  size="sm"
                  dense
                  round
                  @click="toggleSearch"
                >
                  <q-tooltip>Search Table</q-tooltip>
                </q-btn>
                <q-input
                  v-if="!printing && (showSearchField || searchQuery)"
                  ref="searchInput"
                  v-model="internalSearchQuery"
                  class="col-auto search-input"
                  dense
                  borderless
                  placeholder="Search..."
                  style="width: 150px; margin-top: 0px; margin-bottom: 5px"
                  input-style="padding: 4px 8px; line-height: 1; min-height: 20px;"
                  :disable="!!searchQuery"
                  :autofocus="!searchQuery"
                  @update:model-value="$_debouncedEmitSearch"
                >
                  <template #append>
                    <q-icon
                      v-if="!searchQuery"
                      name="close"
                      size="xs"
                      class="cursor-pointer"
                      style="margin-right: 4px"
                      @click="clearSearch"
                    />
                  </template>
                </q-input>
                <q-btn
                  v-if="!printing"
                  class="col-auto q-mr-sm full-screen-button"
                  :icon="isFullScreen ? 'fullscreen_exit' : 'fullscreen'"
                  size="sm"
                  dense
                  round
                  @click="toggleFullScreen"
                >
                  <q-tooltip>{{ isFullScreen ? 'Exit Full Screen' : 'Full Screen' }}</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="!printing"
                  class="col-auto download-link"
                  icon="download"
                  size="sm"
                  dense
                  round
                  @click="$emit('export-data')"
                  @mouseenter="showExportDataOptions"
                  @mouseleave="hideExportDataOptions"
                >
                  <q-menu
                    v-model="showExportMenu"
                    anchor="bottom middle"
                    self="top middle"
                    :offset="[0, 0]"
                    @mouseenter="showExportDataOptions"
                    @mouseleave="hideExportDataOptions"
                  >
                    <q-list dense>
                      <q-item
                        clickable
                        dense
                        @click="$emit('export-data'); showExportMenu = false"
                      >
                        <q-item-section>
                          <q-item-label class="text-caption">
                            Export CSV
                          </q-item-label>
                        </q-item-section>
                      </q-item>
                      <q-item
                        clickable
                        dense
                        @click="$emit('export-data', true); showExportMenu = false"
                      >
                        <q-item-section>
                          <q-item-label class="text-caption">
                            Export CSV with Totals
                          </q-item-label>
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </q-menu>
                </q-btn>
              </div>
              <div
                v-if="reportOptions.custom_description"
                class="text-caption col q-px-md q-pb-sm"
              >
                {{ reportOptions.custom_description }}
              </div>
            </div>
          </template>

          <template #header="props">
            <q-tr
              :props="props"
              @contextmenu.stop.prevent
            >
              <q-th
                v-for="reportTableColumn in props.cols"
                :key="reportTableColumn.name"
                :class="reportTableColumn.groupable ? 'bg-grey-4 group-header-row' : 'group-header-row'"
                :props="props"
              >
                <div class="row items-center">
                  {{ reportTableColumn.label }}
                  <q-btn
                    v-if="reportTableColumn.component === 'multiline-text'"
                    flat
                    dense
                    round
                    size="xs"
                    :icon="expandedColumns[reportTableColumn.field] ? 'unfold_less' : 'unfold_more'"
                    class="q-ml-xs"
                    @click.stop="toggleColumnExpansion(reportTableColumn.field)"
                  >
                    <q-tooltip>
                      {{ expandedColumns[reportTableColumn.field] ? 'Collapse text' : 'Expand text' }}
                    </q-tooltip>
                  </q-btn>
                  <q-tooltip v-if="reportTableColumn.definition">
                    {{ reportTableColumn.definition }}
                  </q-tooltip>
                  <q-icon
                    v-if="!printing && reportTableColumn.groupable"
                    class="q-pl-xs text-grey-10"
                    name="category"
                  />
                  <q-icon
                    v-if="!printing && reportTableColumn.filterable && reportElement.isFieldFiltered(reportTableColumn.field)"
                    class="q-pl-xs text-grey-10"
                    name="filter_alt"
                  />
                </div>
                <report-table-header-menu
                  :report-element="reportElement"
                  :report="report"
                  :report-table-column="reportTableColumn"

                  @edit-columns="$emit('edit-columns')"
                  @edit-groupings="$emit('edit-groupings')"

                  @remove-column="field => removeColumn(field)"
                  @remove-group-by="field => removeGroupBy(field)"
                  @toggle-share="field => toggleShareColumn(field)"

                  @preview-changes="previewChanges()"
                />
              </q-th>
            </q-tr>
          </template>

          <template #body-cell="props">
            <q-td
              v-if="!props.value"
            />
            <q-td
              v-else
              :props="props"
              :class="cellClassPrinting + ' ' + (props.col.groupable ? 'bg-grey-2' : '') + ' ' + (props.row.classes || '') + ' ' + (props.value.cellClasses || '') + ' ' +
                (props.col.sensitive ? 'sensitive-data-inside' : '')"
              :style="{
                'max-width': props.maxWidth || props.col.maxWidth || '225px',
                'min-width': reportElement.reportOptions.group_share_columns?.includes(props.col.field) ? '120px' : (props.minWidth || props.col.minWidth || null)
              }"
              auto-width
              @dblclick="props.col.aggregate ? $emit('show-source', props) : filterTo(props)"
              @contextmenu.stop.prevent
            >
              <template v-if="props.col.groupable && !props.value.isNewGroup" />
              <template v-else-if="props.col.component === 'checkmark' || props.value.component === 'checkmark'">
                <q-icon
                  v-if="props.value.value"
                  name="check"
                  size="md"
                  color="positive"
                />
              </template>
              <template v-else-if="props.col.component === 'checkmark-or-negative' || props.value.component === 'checkmark-or-negative'">
                <q-icon
                  v-if="props.value.value"
                  name="check"
                  size="md"
                  color="positive"
                />
                <q-icon
                  v-else
                  name="remove"
                  size="md"
                  color="negative"
                />
              </template>
              <template v-else-if="props.col.component === 'checkbox' || props.value.component === 'checkbox'">
                <q-checkbox
                  disable
                  :model-value="props.value.value"
                />
              </template>
              <template v-else-if="props.col.component === 'icon' || props.value.component === 'icon'">
                <q-icon
                  v-if="$_.isObject(props.value)"
                  :name="props.value.name"
                  :color="props.value.color"
                  size="md"
                >
                  <q-tooltip v-if="props.value.tooltip">
                    {{ props.value.tooltip }}
                  </q-tooltip>
                </q-icon>
                <q-icon
                  v-else
                  class="text-grey-8"
                  :name="props.value.value"
                  size="md"
                />
              </template>
              <template v-else-if="(props.value.typeName || props.value._variableName) && (props.col.component === 'model' || props.value.component === 'model')">
                <q-chip
                  v-if="props.value.id > 0"
                  dense
                  class="model-chip q-ml-none"
                  :label="props.value.description || props.value.name || props.value.value || (props.value.typeName + ' ' + props.value.id)"
                  :title="props.value.description || props.value.name || props.value.value || (props.value.typeName + ' ' + props.value.id)"
                  :style="{ 'background-color': props.value.color }"
                  :color="props.value.color"
                  :text-color="props.value.textColor"
                  :class="{ 'sensitive-data-inside': props.col.sensitive || (props.value.typeName || props.value._variableName) === 'Site' }"
                />
              </template>
              <template v-else-if="props.col.component === 'chips' || props.value.component === 'chips'">
                <div
                  v-if="props.value.hasValue"
                  class="row"
                >
                  <q-chip
                    v-for="value in $_.flatten([props.value.value || props.value])"
                    :key="value && value.value || value"
                    class="col-auto"
                    :icon="value.icon"
                    :label="value.label || value.name || value.value || value"
                    :title="value.label || value.name || value.value"
                    :style="{ 'background-color': value.color }"
                    :color="value.color"
                    :text-color="value.textColor"
                    :size="value.size || 'md'"
                    :dense="value.dense || true"
                    :square="value.square || false"
                  >
                    <q-tooltip v-if="value.tooltip">
                      {{ value.tooltip }}
                    </q-tooltip>
                  </q-chip>
                </div>
              </template>
              <template v-else-if="props.col.component === 'chip' || (props.value.component === 'chip')">
                <q-chip
                  v-if="props.value.hasValue"
                  :icon="props.value.icon"
                  :label="props.value.label || props.value.name || props.value.value"
                  :title="props.value.label || props.value.name || props.value.value"
                  :style="{ 'background-color': props.value.color }"
                  :color="props.value.color"
                  :text-color="props.value.textColor"
                  :size="props.value.size || 'md'"
                  :dense="props.value.dense || true"
                  :square="props.value.square || false"
                >
                  <q-tooltip v-if="props.value.tooltip">
                    {{ props.value.tooltip }}
                  </q-tooltip>
                </q-chip>
              </template>
              <template v-else-if="props.col.component === 'img' || (props.value.component === 'img')">
                <img
                  v-if="props.value"
                  height="60px"
                  :src="props.value"
                >
              </template>
              <template v-else-if="props.col.component === 'color' || (props.value.component === 'color')">
                <div
                  v-if="props.value"
                  style="border: solid 2px; height: 40px; width: 40px; border-radius: 4px;"
                  :style="'color: ' + props.value + '; border-color: ' + props.value + '; background-color: ' + props.value + '33;'"
                />
              </template>
              <template
                v-else-if=" isComponentSourceCents(props.value.component)|| isComponentSourceCents(props.col.component)
                "
              >
                <span v-if="!props.value || !$_.isNumber(props.value.amount || props.value.value)">—</span>
                <i18n-n
                  v-else
                  :class="{ 'text-grey-7': !(props.value.amount || props.value.value), 'text-red': (props.value.amount || props.value.value) < 0 }"
                  tag="span"
                  :value="(props.value.amount || props.value.value) / 100"
                  format="currency"
                  scope="global"
                />
              </template>
              <template v-else-if="props.col.component === 'money' || (props.value.component === 'money')">
                <span v-if="!props.value || !$_.isNumber(props.value.amount || props.value.value)">—</span>
                <i18n-n
                  v-else
                  :class="{ 'text-grey-7': !(props.value.amount || props.value.value), 'text-red': (props.value.amount || props.value.value) < 0 }"
                  tag="span"
                  :value="props.value.amount || props.value.value"
                  format="currency"
                  scope="global"
                />
              </template>
              <template v-else-if="props.col.component === 'money-rounded' || (props.value.component === 'money-rounded')">
                <span v-if="!props.value || !$_.isNumber(props.value.amount || props.value.value)">—</span>
                <i18n-n
                  v-else
                  :class="{ 'text-grey-7': !(props.value.amount || props.value.value), 'text-red': (props.value.amount || props.value.value) < 0 }"
                  tag="span"
                  :value="props.value.amount || props.value.value"
                  format="currencyNoCents"
                  scope="global"
                />
              </template>
              <template v-else-if="props.col.component === 'money-hundredths' || (props.value.component === 'money-hundredths')">
                <span v-if="!props.value || !$_.isNumber(props.value.amount || props.value.value)">—</span>
                <i18n-n
                  v-else
                  :class="{ 'text-grey-7': !(props.value.amount || props.value.value), 'text-red': (props.value.amount || props.value.value) < 0 }"
                  tag="span"
                  :value="props.value.amount || props.value.value"
                  format="currencyHundredths"
                  scope="global"
                />
              </template>
              <template v-else-if="props.col.component === 'money-count' || (props.value.component === 'money-count')">
                <span v-if="!props.value || !$_.isNumber(props.value.amount)">—</span>
                <i18n-n
                  v-else
                  :class="{ 'text-grey-7': !(props.value.amount), 'text-red': (props.value.amount) < 0 }"
                  tag="span"
                  :value="props.value.amount"
                  format="currency"
                  scope="global"
                />
                <q-chip
                  v-if="props.value.count"
                  square
                  dense
                  color="grey-7"
                  text-color="white"
                  size="sm"
                  class="q-ml-sm"
                  :label="props.value.count"
                />
              </template>
              <template v-else-if="props.col.component === 'integer-value' || (props.value.component === 'integer-value')">
                <span v-if="!props.value || !$_.isNumber(props.value.amount || props.value.value)">—</span>
                <i18n-n
                  v-else
                  :class="{ 'text-grey-7': !(props.value.amount || props.value.value), 'text-red': (props.value.amount || props.value.value) < 0 }"
                  tag="span"
                  :value="props.value.amount || props.value.value"
                  format="decimal"
                  scope="global"
                />
              </template>
              <template v-else-if="isDecimalComponent(props.col.component) || isDecimalComponent(props.value.component)">
                <span v-if="!props.value || !$_.isNumber(props.value.amount || props.value.value)">—</span>
                <i18n-n
                  v-else
                  :class="{ 'text-grey-7': !(props.value.amount || props.value.value), 'text-red': (props.value.amount || props.value.value) < 0 }"
                  tag="span"
                  :value="props.value.amount || props.value.value"
                  :format="getDecimalFormat(props.col.component, props.value.component)"
                  scope="global"
                />
              </template>
              <template v-else-if="(props.col.component === 'percent' || (props.value.component === 'percent')) && (props.value.value || props.value.value === 0)">
                <span v-if="!props.value || !$_.isNumber(props.value.amount || props.value.value)">—</span>
                <i18n-n
                  v-else
                  :class="{ 'text-grey-7': !(props.value.amount || (props.value.value) || props.value), 'text-red': (props.value.amount || (props.value.value) || props.value) < 0 }"
                  tag="span"
                  :value="props.value.amount || props.value.value"
                  format="decimal1"
                  scope="global"
                /><span :class="{ 'text-grey-7': !(props.value.amount || (props.value.value) || props.value), 'text-red': (props.value.amount || (props.value.value) || props.value) < 0 }">%</span>
              </template>
              <template v-else-if="(props.col.component === 'percent-2' || (props.value.component === 'percent-2')) && (props.value.value || props.value.value === 0)">
                <span v-if="!props.value || !$_.isNumber(props.value.amount || props.value.value)">—</span>
                <i18n-n
                  v-else
                  :class="{ 'text-grey-7': !(props.value.amount || (props.value.value) || props.value), 'text-red': (props.value.amount || (props.value.value) || props.value) < 0 }"
                  tag="span"
                  :value="props.value.amount || props.value.value"
                  format="decimal2"
                  scope="global"
                /><span :class="{ 'text-grey-7': !(props.value.amount || (props.value.value) || props.value), 'text-red': (props.value.amount || (props.value.value) || props.value) < 0 }">%</span>
              </template>
              <template v-else-if="props.col.component === 'relative-time' || (props.value.component === 'relative-time')">
                <q-chip
                  v-if="props.value"
                  square
                  dense
                >
                  {{ props.value.relativeTime && props.value.relativeTime() || (props.value.value) || props.value }}
                  <q-tooltip v-if="props.value.relativeTime">
                    {{ props.value }}
                  </q-tooltip>
                </q-chip>
              </template>
              <template v-else-if="(props.col.component === 'date' || (props.value.component === 'date')) && !isNaN(Date.parse(props.value.value))">
                <q-chip
                  v-if="!isNaN(Date.parse(props.value.value))"
                  square
                  dense
                >
                  {{ IsoDate.parse(props.value.value).toLocaleDateString() }}
                </q-chip>
                <span v-else-if="props.value.value">
                  {{ props.value.value }}
                </span>
              </template>
              <template v-else-if="props.value.isDate && (props.col.component === 'full-date' || props.value.component === 'full-date')">
                <q-chip
                  square
                  dense
                >
                  {{ IsoDate.parse(props.value.value).format('ddd, MMM Do, YYYY') }}
                </q-chip>
              </template>
              <template v-else-if="props.col.component === 'date-time' || (props.value.component === 'date-time')">
                <q-chip
                  v-if="props.value.value"
                  square
                  dense
                >
                  {{ new IsoDateTime(props.value.value).inTimeZone(timeZone).toLocaleDateString() }}
                  {{ new IsoDateTime(props.value.value).inTimeZone(timeZone).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
                </q-chip>
              </template>
              <template v-else-if="props.col.component === 'time' || (props.value.component === 'time')">
                <q-chip
                  v-if="props.value"
                  square
                  dense
                >
                  {{ new IsoDateTime(props.value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
                </q-chip>
              </template>
              <template v-else-if="props.col.component === 'movie-rating'">
                <q-btn
                  v-if="props.value.value"
                  color="yellow-7"
                  outline
                  size="sm"
                  icon="star"
                  round
                >
                  {{ props.value.value }}
                  <q-tooltip>
                    Rated {{ props.value.value }} out of 5 Stars
                  </q-tooltip>
                </q-btn>
              </template>
              <template v-else-if="props.col.component === 'visit-rating'">
                <q-icon
                  v-if="props.value.rating"
                  :color="props.value.color"
                  :name="RATING_ICONS[props.value.rating - 1]"
                  size="md"
                >
                  <q-tooltip>
                    Rated {{ props.value.rating }} out of 4
                  </q-tooltip>
                </q-icon>
              </template>
              <template v-else-if="props.col.component === 'visit-rating-average'">
                <q-icon
                  v-if="props.value.value"
                  :color="RATING_ICONS_COLORS[Math.round(props.value.value) - 1]"
                  :name="RATING_ICONS[Math.round(props.value.value) - 1]"
                  size="md"
                >
                  <q-tooltip>
                    Average Rating {{ Math.round(props.value.value * 100) / 100 }} out of 4
                  </q-tooltip>
                </q-icon>
              </template>
              <template v-else-if="props.col.component === 'multiline-text'">
                <div class="row items-start">
                  <div :class="{ 'ellipsis-3-lines': !expandedColumns[props.col.field] }">
                    {{ props.value.value }}
                    <q-tooltip v-if="!expandedColumns[props.col.field]">
                      {{ props.value.value }}
                    </q-tooltip>
                  </div>
                </div>
              </template>
              <template v-else-if="props.col.component === 'html'">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div v-html="props.value.value" />
              </template>
              <template v-else-if="props.col.component === 'effective-type'">
                <q-chip
                  v-if="props.value.value"
                  dense
                  square
                  class="q-ml-none"
                  :label="props.value.label || props.value.value"
                  :style="{ 'background-color': props.value.color }"
                  :clickable="!!props.value.label"
                  @click="props.value.label && showEffectiveDialog(props.value.value)"
                >
                  <q-tooltip v-if="props.value.label">
                    <effective-type-detail
                      style="max-width: 600px;"
                      :effective-type="props.value.value"
                      :show-void-control="false"
                    />
                  </q-tooltip>
                </q-chip>
              </template>
              <template v-else-if="props.value.value === 0">
                <span>—</span>
              </template>
              <template v-else-if="isGenericColComponent(props.col.component)">
                <component
                  :is="props.col.component"
                  v-bind="{ props: props }"
                />
              </template>
              <template v-else>
                <div class="text-no-wrap ellipsis">
                  {{ (props.value.label || props.value.name || props.value.value) }}
                </div>
              </template>
              <template v-if="reportElement.reportOptions.group_share_columns?.includes(props.col.field) && props.value.shareValue">
                <span
                  class="q-ml-xs share-value-chip q-chip row inline no-wrap items-center text-caption"
                >
                  <i18n-n
                    :value="props.value.shareValue"
                    format="decimal1"
                    scope="global"
                  />%
                </span>
              </template>

              <report-table-cell-menu
                :report="report"
                :report-element="reportElement"
                :report-table-column="props.col"
                :cell="props"
                @edit-filter="$emit('edit-filters')"
                @preview-changes="previewChanges()"
                @show-source="cell => $emit('show-source', cell)"
                @show-data-source="cell => $emit('show-data-source', cell)"
                @show-trend="cell => $emit('show-trend', cell)"
                @show-drill-down="cell => $emit('show-drill-down', cell)"
                @show-comparison="cell => $emit('show-comparison', cell)"
                @edit-model="handleRowClick(props, props.row)"
              />
            </q-td>
          </template>

          <template
            #body-cell-actions="props"
          >
            <q-td
              :props="props"
              :class="cellClassPrinting"
            />
          </template>

          <template #no-data="{ icon, message }">
            <div
              v-if="!printing && !reportStatus.done"
              class="full-width row justify-between"
            >
              <report-migratable-button
                v-if="!editing && reportElement.migratable"
                :class="{ 'data-freshness': reportStatus.done }"
                size="sm"
                @edit="$emit('edit', true)"
              />
              <report-status
                :class="{ 'data-freshness': reportStatus.done }"
                class="col-auto"
                size="sm"
                :report-status="reportStatus"
              />
              <div
                v-if="reportStatus.errorMessage"
                class="col-auto text-negative"
              >
                {{ reportStatus.errorMessage }}
              </div>
            </div>
            <div
              v-else
              class="full-width row items-center q-gutter-sm"
            >
              <q-icon
                size="2em"
                :name="icon"
              />
              <span>
                {{ message }}
              </span>
            </div>
          </template>
        </q-table>
      </div>

      <div
        v-if="hasMore"
        class="row items-center bg-grey-4"
        :class="{ 'col-auto': isFullScreen }"
        style="padding: 5px 5px;"
      >
        <div class="col-auto">
          Viewing {{ finalRows.length }} of {{ processedRows.length }} rows
        </div>
        <q-space />
        <q-btn
          class="col-auto q-mr-sm"
          color="primary"
          outline
          size="sm"
          :label="$t('actions.loadMore')"
          @click="maxRows += 100"
        />
        <q-btn
          class="col-au"
          outline
          size="sm"
          label="Export All"
          @click="$emit('export-data')"
        />
      </div>
    </div>
    <effective-type-dialog
      v-model="showEffectiveTypeDialog"
      :effective-type="effectiveType"
      mode="documentation"
      @select="reportOptions.effective_type = $event"
    />
    <report-table-information-dialog
      v-model="showInformationDialog"
      :report-element="reportElement"
      :report-options="reportOptions"
      :visible-columns="visibleColumns"
      @edit-element="$emit('edit-element', reportElement)"
      @edit-columns="$emit('edit-columns')"
      @edit-groupings="$emit('edit-groupings')"
      @edit-effective-type="showEffectiveDialog(reportOptions.effective_type)"
    />
  </div>
</template>

<script>
import { appEnv } from 'src/config'
import { IsoDate, IsoDateTime } from 'src/models/model'
import { isComponentSourceCents, NUMERIC_COMPONENT_TYPES } from 'src/models/report/ReportElement'
import { internalSort, standardSortValue } from 'src/models/report/ReportTable'
import { RATING_ICONS, RATING_ICONS_COLORS } from 'src/models/visit-review/VisitReview'
import EffectiveTypeDetail from 'src/modules/admin/effective-type/EffectiveTypeDetail'
import EffectiveTypeDialog from 'src/modules/admin/effective-type/EffectiveTypeDialog'
import ReportMigratableButton from 'src/modules/admin/report/ReportMigratableButton'
import ReportModelMenu from 'src/modules/admin/report/ReportModelMenu'
import ReportStatus from 'src/modules/admin/report/ReportStatus'
import ReportTableCellMenu from 'src/modules/admin/report-table/ReportTableCellMenu'
import ReportTableHeaderMenu from 'src/modules/admin/report-table/ReportTableHeaderMenu'
import ReportTableInformationDialog from 'src/modules/admin/report-table/ReportTableInformationDialog'

export default {
  name: 'ReportTable',
  components: {
    ReportModelMenu,
    ReportStatus,
    ReportMigratableButton,
    ReportTableCellMenu,
    ReportTableHeaderMenu,
    EffectiveTypeDetail,
    EffectiveTypeDialog,
    ReportTableInformationDialog
  },
  props: {
    table: {
      type: Object,
      required: true
    },
    report: {
      type: Object,
      required: true
    },
    reportElement: {
      type: Object,
      required: true
    },
    reportOptions: {
      type: Object,
      required: true
    },
    reportStatus: {
      type: Object,
      required: false,
      default: null
    },
    showFilter: {
      type: Boolean,
      required: false,
      default: undefined
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    cellClass: {
      type: Function,
      required: false,
      default: null
    },
    rowKey: {
      type: String,
      required: false,
      default: 'rowKey'
    },
    printing: {
      type: Boolean,
      required: false,
      default: false
    },
    editing: {
      type: Boolean,
      required: false,
      default: false
    },
    repeatHeaders: {
      type: Boolean,
      required: false,
      default: false
    },
    timeZone: {
      type: String,
      required: true
    },
    searchQuery: {
      type: String,
      default: ''
    },
    simpleView: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['export-data', 'edit-columns', 'edit-groupings', 'show-source', 'edit-filters', 'show-data-source', 'show-trend', 'show-drill-down',
    'show-comparison', 'edit', 'edit-element',
    'update:searchQuery'],
  data () {
    return {
      model: null,
      filter: '',
      internalSearchQuery: '',
      showSearchField: false,
      showExportMenu: false,
      leaveExportDataMenu: null,
      pagination: {
        rowsPerPage: 0,
        sortBy: this.reportElement.reportOptions?.sort_by,
        descending: this.reportElement.reportOptions?.descending || false
      },

      subtotals: true,
      grandTotal: true,
      grandAverage: true,

      expandedColumns: {},

      processingRows: false,
      didProcessRows: false,
      processedRows: [],
      readyToPrint: false,
      previousSortByState: null,

      effectiveType: null,
      showEffectiveTypeDialog: false,
      showInformationDialog: false,

      maxRows: this.printing ? 500 : 100,
      hasHorizontalOverflow: false,
      isFullScreen: false

    }
  },
  computed: {
    noDataLabel () {
      if (this.table.loading) {
        return 'Loading data...'
      } else if (this.processingRows) {
        return 'Processing rows...'
      }
      return 'No data'
    },
    visibleColumns () {
      return _.filter(
        this.table.columns.data,
        column =>
          !_.includes(this.table.hiddenColumns, column.field) &&
          !column.hide &&
          !column.groupable // Groups are handled automatically
      )
    },
    sortedColumns () {
      // First columns are always the group by columns in the order of the grouping
      const columns = []

      // Add group by fields
      const groupByColumns =
        _(this.reportOptions.group_by)
          .map(field => _.find(this.table.columns.data, { field, groupable: true }))
          .compact()
          .value()

      columns.push(...groupByColumns)

      const selectedColumnsInOrder =
        this.table.columnOrder
          ? _(this.table.columnOrder)
            .map(field => _.find(this.visibleColumns, { field }))
            .compact()
            .value()
          : this.visibleColumns
      columns.push(...selectedColumnsInOrder)

      return columns
    },
    processedColumns () {
      const columns = _.map(this.sortedColumns, column => {
        if (column.hide) return null

        // Remove null values from column definition
        column = _.omitBy(column, _.isNil)

        column = { ...column.data }

        column.headerStyle ||= 'vertical-align: top;'

        if (column.groupable) {
          column.sortable = true
        }

        if (column.groupable) {
          column.align = 'left'
        } else if ((column.summable || column.averageable || column.aggregate) && !column.align) {
          column.align = 'right'
          column.headerStyle += 'text-align: left;'
        } else if (!column.align) {
          column.align = 'left'
        }

        const key = (column.name || column.field)

        if (!column.label && column.field === 'id') {
          column.label = 'ID'
        }
        if (!column.label) {
          column.label = _.startCase(key)
        }
        if (!column.name) {
          column.name = column.field
        }
        if (column.sortable === null || column.sortable === undefined) {
          column.sortable = true
        } else if (column.sortable === 'true') {
          column.sortable = true
        } else if (column.sortable === 'false') {
          column.sortable = false
        }

        return column
      })

      return _.compact(columns)
    },
    groupableFields () {
      return _(this.processedColumns).filter('groupable').map('field').reverse().value()
    },
    summableFields () {
      return _(this.table.columns.data).filter('summable').map('field').value()
    },
    areAggregateFields () {
      return _.some(this.processedColumns, (col) => col.summable || col.computedType)
    },
    calculatedFields () {
      return _(this.processedColumns).filter('computedType').map('field').value()
    },
    otherFields () {
      return _(this.processedColumns)
        .map('field')
        .without(...this.groupableFields)
        .without(...this.summableFields)
        .without(...this.calculatedFields)
        .value()
    },
    wrappedRows () {
      // Ensure every cell is an object
      const allColumns = this.table.columns.data
      const wrapped = _.map(this.table.tableData, (row) => {
        const nonObservableRow = { ...row }

        _.each(allColumns, column => {
          this.ensureWrappedValue(nonObservableRow, column.field)
        })

        // We use the key for consolidated groupings, ensure this is wrapped too
        this.ensureWrappedValue(nonObservableRow, 'key')
        if (!nonObservableRow.rowKey) {
          nonObservableRow.rowKey = nonObservableRow.key.value
        }

        return nonObservableRow
      })

      // Filter rows before any processing if search is active
      if (this.searchFilter) {
        const searchTerms = _.filter(this.searchFilter.toLowerCase().split(/\s+/), term => term.length > 0)
        return _.filter(wrapped, row => {
          // Check if the row matches all search terms
          return _.every(searchTerms, term => {
            // For each term, at least one column must contain it
            return _.some(this.table.columns.data, column => {
              const cellValue = row[column.field]?.value
              if (cellValue === null || cellValue === undefined) return false
              return String(cellValue).toLowerCase().includes(term)
            })
          })
        })
      }

      return wrapped
    },
    finalRows () {
      return this.processedRows.slice(0, this.maxRows)
    },
    hasMore () {
      return this.finalRows.length < this.processedRows.length
    },
    groupedRows () {
      return this.groupRows(this.wrappedRows, this.reportOptions.group_by)
    },
    shouldShowFilter () {
      return this.showFilter
    },
    cellClassPrinting () {
      return [this.printing && 'q-td--no-hover', this.cellClass && this.cellClass(this.props)].filter(Boolean).join(' ')
    },
    searchFilter () {
      return this.internalSearchQuery || this.searchQuery || ''
    },
    hasCustomDescription () {
      return !!(this.reportOptions.custom_description)
    }
  },
  watch: {
    'reportStatus.done': {
      handler () {
        if (this.reportStatus.done && !this.readyToPrint) {
          // Allow some time observe table data. If table data is empty,
          // allow the empty table to be printed.
          setTimeout(() => {
            if (!this.table.tableData.length) {
              this.readyToPrint = true
            }
          }, 1000)
        }
      },
      immediate: true
    },
    wrappedRows: {
      handler () {
        if (!this.debouncedComputeProcessedRows) {
          this.debouncedComputeProcessedRows = _.debounce(() => {
            this.checkTableOverflow()
            this.processedRows = this.computeProcessedRows()
          }, 100)
        }

        this.debouncedComputeProcessedRows()
      },
      immediate: true
    },
    pagination () {
      if (
        this.reportElement.reportOptions.sort_by === this.pagination.sortBy &&
        this.reportElement.reportOptions.descending === this.pagination.descending
      ) return

      if (this.reportElement.reportOptions.sort_by !== this.pagination.sortBy) {
        if (this.previousSortByState === this.pagination.sortBy) {
        // This means user selected the sort column through the report control UI,
        // so we want to set the pagination option to the report option
          this.pagination.sortBy = this.reportElement.reportOptions.sort_by
        } else {
          this.reportElement.reportOptions.sort_by = this.pagination.sortBy
        }
        this.previousSortByState = this.reportElement.reportOptions.sort_by
      }
      // Only set the descending option if the user has explicitly set a sort
      // otherwise this will mark the report dirty when opening the report
      // and an no sort by is set
      if (this.reportElement.reportOptions.sort_by) {
        this.reportElement.reportOptions.descending = this.pagination.descending
      }

      this.previewChanges()
    },
    'reportElement.reportOptions.sortBy' () {
      if (this.pagination.sortBy === this.reportElement.reportOptions.sort_by) return

      this.pagination.sortBy = this.reportElement.reportOptions.sort_by
    },
    'reportElement.reportOptions.descending' () {
      if (this.pagination.descending === this.reportElement.reportOptions.descending) return

      this.pagination.descending = this.reportElement.reportOptions.descending
    },
    searchQuery: {
      immediate: true,
      handler (newVal) {
        if (newVal !== this.internalSearchQuery) {
          this.internalSearchQuery = newVal
          if (newVal) {
            this.showSearchField = true
          } else {
            this.showSearchField = false
          }
        }
      }
    }
  },
  created () {
    _.extend(this, { IsoDate, IsoDateTime, RATING_ICONS, RATING_ICONS_COLORS, isComponentSourceCents })

    this.hasHeaderBanner = appEnv !== 'production'
    this.$_debouncedEmitSearch = _.debounce((value) => {
      this.$emit('update:searchQuery', value)
    }, 280)
  },
  mounted () {
    this.checkTableOverflow()
    window.addEventListener('resize', this.checkTableOverflow)
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.checkTableOverflow)
    if (this.leaveExportDataMenu) {
      clearTimeout(this.leaveExportDataMenu)
    }
  },
  // Rendering our data tables often takes some time and multiple renders,
  // so it's hard to know when exactly the table is finished with DOM updates
  // and is ready to be printed. We use a time buffer to wait for all renders.
  updated () {
    if (this.didProcessRows && !this.readyToPrint) {
      if (!this.debouncedSetReadyToPrint) {
        this.debouncedSetReadyToPrint = _.debounce(() => {
          this.readyToPrint = true
        }, 2000)
      }
      this.debouncedSetReadyToPrint()
    }
  },
  methods: {
    ensureWrappedValue (nonObservableRow, field) {
      let value = nonObservableRow[field]
      if (!_.isObject(value) || _.isArray(value)) {
        value = { value }
      }
      value.hasValue = !!(value.value || value.value === false || value.value === 0)
      value.isDate = !isNaN(Date.parse(value.value))
      value.isNumber = !isNaN(parseFloat(value.value))
      value.cellClasses = '' // Reset cell classes
      nonObservableRow[field] = value
    },
    showExportDataOptions () {
      clearTimeout(this.leaveExportDataMenu)
      this.showExportMenu = true
    },
    hideExportDataOptions () {
      this.leaveExportDataMenu = setTimeout(() => {
        this.showExportMenu = false
      }, 100)
    },
    computeProcessedRows () {
      this.processingRows = true
      let rows = this.wrappedRows

      const allColumns = [...this.table.columns.data]
      const displayColumns = this.processedColumns

      if (this.reportElement.finalOptions.top_n) {
        const sortedRows = [...rows].sort((a, b) => {
          const sortBy = this.reportElement.reportOptions.sort_by || displayColumns[displayColumns.length - 1].field

          const xValue = standardSortValue(a[sortBy])
          const yValue = standardSortValue(b[sortBy])

          if (_.isString(xValue) || _.isString(yValue)) {
            // string sort
            return xValue > yValue ? 1 : xValue < yValue ? 0 : -1
          } else {
            // numeric sort
            return parseFloat(yValue) - parseFloat(xValue)
          }
        })

        if (!this.reportElement.reportOptions.descending) {
          sortedRows.reverse()
        }

        const topRows = sortedRows.splice(0, this.reportElement.finalOptions.top_n)

        rows = _.intersection(rows, topRows)
      }

      if (this.reportOptions.group_by?.length) {
        const groupedRows = this.groupRows(rows, this.reportOptions.group_by)

        const sortedGroupedRows = this.sortGroupedRows(groupedRows, this.reportElement.reportOptions.sort_by, this.reportElement.reportOptions.descending, this.reportElement.reportOptions.sort_groups)

        rows = this.flattenGroupedRows(sortedGroupedRows)
      }

      // Add group indicators
      if (this.reportOptions.group_by?.length) {
        const lastGroupValues = {}
        let isNewGroup = true
        _.each(rows, (row, rowIndex) => {
          _.each(displayColumns, (column, columnIndex) => {
            const field = column.field

            const value = row[field]

            if (column.groupable && lastGroupValues[field] !== value.value) isNewGroup = true

            if (isNewGroup) {
              value.isNewGroup = true
              value.cellClasses = (value.cellClasses || '') + 'new-group-cell '
              if (column.groupable) {
                value.cellClasses = (value.cellClasses || '') + 'new-groupable '
              }

              const priorRow = rows[rowIndex - 1]
              const priorCell = priorRow && priorRow[field]
              if (priorCell) {
                priorCell.cellClasses = (priorCell.cellClasses || '') + 'last-of-group '
              }
            } else if (column.groupable && !isNewGroup) {
              value.isNewGroup = false
              value.cellClasses = (value.cellClasses || '') + 'not-new-group '
            }

            const nextColumn = displayColumns[columnIndex + 1]
            if (column.groupable && (!nextColumn || !nextColumn.groupable)) {
              value.cellClasses = (value.cellClasses || '') + 'last-group '
              value.isLastGroup = true
            }

            lastGroupValues[field] = value.value
          })
          isNewGroup = false
        })

        if (this.reportOptions.subtotals) {
          const newRows = []

          const groupTotals = {}

          _.each(_.reverse(rows), (row, rowIndex) => {
            newRows.push(row)
            _.each(this.summableFields.concat(this.groupableFields), field => {
              const column = _.find(allColumns, { field })

              if (!column) return

              const cell = row[field]

              if (column.summable) {
                // Add value to all groupable types
                _.each(this.groupableFields, groupField => {
                  groupTotals[groupField] ||= {}
                  groupTotals[groupField][field] ||= 0

                  groupTotals[groupField][field] += cell.value || 0
                })
              } else if (column.groupable) {
                if ((cell.isNewGroup && !cell.isLastGroup) || rowIndex === rows.length) {
                  // Add subtotal, reset counts
                  const subtotalRow = {
                    [field]: {
                      ...cell,
                      cellClasses: cell.cellClasses + ' subgroup text-bold text-grey-9',
                      isNewGroup: true
                    }
                  }

                  let subsubgroup = true
                  _.each(this.groupableFields, groupField => {
                    if (groupField === field) {
                      subsubgroup = false
                      return
                    }

                    subtotalRow[groupField] = {
                      cellClasses: subsubgroup ? ' sub-subgroup' : ' super-subgroup',
                      isNewGroup: true
                    }
                  })

                  // Mark the prior new group cell as not the new group
                  cell.isNewGroup = false

                  const fieldGroupTotals = groupTotals[field]
                  _.each(fieldGroupTotals, (total, summableField) => {
                    // Set total for this group
                    subtotalRow[summableField] = {
                      ...row[summableField],
                      value: total,
                      cellClasses: row[summableField].cellClasses + ' subtotal-value text-bold text-grey-9',
                      subtotalValue: true
                    }

                    // Reset
                    fieldGroupTotals[summableField] = 0
                  })

                  _.each(this.calculatedFields, calculatedField => {
                    const column = _.find(allColumns, { field: calculatedField })

                    // Compute this value
                    subtotalRow[calculatedField] = {
                      ...row[calculatedField],
                      value: column.calculateValue(subtotalRow),
                      cellClasses: row[calculatedField].cellClasses + ' subtotal-value text-bold text-grey-9',
                      subtotalValue: true
                    }
                  })

                  _.each(this.otherFields, otherField => {
                    subtotalRow[otherField] = {
                      ...row[otherField],
                      value: null,
                      hasValue: false
                    }
                  })

                  subtotalRow.groupField = field

                  newRows.push(subtotalRow)
                }
              }
            })
          })

          rows = _.reverse(newRows)
        }
      }

      if (this.grandTotal && this.areAggregateFields && rows.length > 0) {
        const totalRow = {}

        _.each(allColumns, column => {
          const field = column.field

          if (!column) return

          _.each(rows, (row, rowIndex) => {
            const isLastGroup = this.groupableFields && this.groupableFields[this.groupableFields.length - 1] === field
            if (column.groupable && isLastGroup && rowIndex === 0) {
              totalRow[field] = { value: 'Total', isNewGroup: true }
            }

            if (column.summable && !column.hideTotals && !(row[field] && row[field].subtotalValue)) {
              totalRow[field] ||= { value: 0 }
              totalRow[field].value += (row[field].value || 0)
            }

            if (!column.summable || column.hideTotals) {
              totalRow[field] ||= { value: '' }
            }
          })
        })

        _.each(this.calculatedFields, calculatedField => {
          const column = _.find(allColumns, { field: calculatedField })

          // Compute this value
          totalRow[calculatedField] = {
            ...totalRow[calculatedField],
            value: column.calculateValue(totalRow)
          }
        })

        totalRow.grandTotal = true
        totalRow.classes = 'grand-total text-bold text-grey-9'

        // Fixes floating point rounding errors
        // Calculation may result in a miniscule negative value, which displays as -0.00
        if (totalRow.amount?.value && Number(totalRow.amount.value.toFixed(2)) === 0.00) {
          totalRow.amount.value = 0.00
        }

        rows.push(totalRow)
      }

      // Update group dependent computed values
      _.each(displayColumns, column => {
        const isGroupSharePercent = column.computedType === 'group_share_percent'
        const isShareColumn = this.reportElement.reportOptions.group_share_columns?.includes(column.field)
        const isNumericComponent = NUMERIC_COMPONENT_TYPES.includes(column.component)

        if (!isGroupSharePercent && !(isShareColumn && isNumericComponent)) return

        let outerGroupRow = rows[rows.length - 1]
        let priorGroupField = null

        _.each(this.reportOptions.group_by, groupField => {
          _.each(rows, (row, index) => {
            if (rows.length - 1 === index) return

            if (row.groupField === priorGroupField) {
              outerGroupRow = row
            } else if (row.groupField === groupField) {
              if (isGroupSharePercent) {
                row[column.field].value = row[column.sourceField].value / outerGroupRow[column.sourceField].value * 100
              }
              if (isShareColumn && isNumericComponent) {
                row[column.field].shareValue = row[column.field].value / outerGroupRow[column.field].value * 100
              }
            } else if (!row.groupField) {
              if (isGroupSharePercent) {
                row[column.field].value = row[column.sourceField].value / outerGroupRow[column.sourceField].value * 100
              }
              if (isShareColumn && isNumericComponent) {
                row[column.field].shareValue = row[column.field].value / outerGroupRow[column.field].value * 100
              }
            }
          })
          priorGroupField = groupField
        })
      })

      this.processingRows = false
      this.didProcessRows = true

      if (!this.reportOptions.group_by?.length) {
        rows = internalSort(rows, this.reportElement.reportOptions.sort_by, this.reportElement.reportOptions.descending)
      }
      return rows
    },
    // Recursively group rows by the given fields
    groupRows (rows, groupByFields) {
      if (!groupByFields.length) return null

      groupByFields = _.flatten([groupByFields])
      const groupByField = groupByFields[0]
      const groupedRows = _.groupBy(rows, row => row[groupByField]?.value)

      return _.map(groupedRows, rows => ({
        rows,
        groupedRows: groupByFields.length ? this.groupRows(rows, groupByFields.slice(1)) : null
      }))
    },
    // Recursively sort grouped rows by the given field
    sortGroupedRows (groups, sortBy, descending, sortGroups = false) {
      if (groups.length === 0) {
        return groups
      }

      _.each(groups, group => {
        // Deep first
        if (group.groupedRows) {
          group.groupedRows = this.sortGroupedRows(group.groupedRows, sortBy, descending)
        } else {
          group.rows = internalSort(group.rows, sortBy, descending)
        }

        // Compute sort values
        let sortValue = 0
        // Check to see if column has all numeric sort values
        // If it does, the sort value is the sum. If not, it is the first non-blank value
        if (group.rows[0]?.[sortBy]?.isNumber) {
          _(group.rows).each(row => {
            const currentValue = row[sortBy]?.value || 0
            if (_.isString(currentValue)) {
              sortValue = currentValue
            } else {
              sortValue += currentValue
            }
          })
        } else {
          sortValue = (group.rows[0]?.[sortBy]?.value || '').trim()
        }
        group.sortValue = sortValue
      })

      // Now sort groups
      const isLeaf = !groups[0].groupedRows
      if (sortGroups || isLeaf) {
        groups = _.sortBy(groups, 'sortValue')
        if (descending) {
          groups = groups.reverse()
        }
      }

      return groups
    },
    // Recursively flatten grouped rows
    flattenGroupedRows (groups) {
      const rows = []

      _.each(groups, group => {
        if (group.groupedRows) {
          rows.push(...this.flattenGroupedRows(group.groupedRows))
        } else {
          rows.push(...group.rows)
        }
      })

      return rows
    },
    handleRowClick (_event, row) {
      if (!row._url) return

      if (row.row_count && row.row_count > 1) return

      window.open('#' + row._url)
    },
    customSort (rows, sortBy, descending) {
      this.reportElement.reportOptions.descending = descending
      return rows
    },
    removeColumn (field) {
      this.reportElement.reportOptions.column_order = _.without(this.reportElement.finalOptions.column_order, field)
      this.previewChanges()
    },
    removeGroupBy (field) {
      this.reportElement.reportOptions.group_by = _.without(this.reportElement.finalOptions.group_by, field)
      this.previewChanges()
    },
    toggleShareColumn (field) {
      const column = _.find(this.table.columns.data, { field })
      if (column) {
        this.reportElement.reportOptions.group_share_columns = this.reportElement.reportOptions.group_share_columns || []
        if (this.reportElement.reportOptions.group_share_columns.includes(field)) {
          this.reportElement.reportOptions.group_share_columns = _.without(this.reportElement.reportOptions.group_share_columns, field)
        } else {
          this.reportElement.reportOptions.group_share_columns.push(field)
        }
        this.previewChanges()
      }
    },
    previewChanges () {
      this.report.debouncedRunReport()
    },
    filterTo (cell) {
      this.reportElement.filterToFieldValue(cell.col.field, cell.value || cell)
      this.previewChanges()
    },
    showEffectiveDialog (effectiveType) {
      this.effectiveType = effectiveType
      this.showEffectiveTypeDialog = true
    },
    isGenericColComponent (colComponent) {
      return colComponent &&
      colComponent !== 'model' &&
      colComponent !== 'full-date' &&
      colComponent !== 'date' &&
      !_.startsWith(colComponent, 'percent')
    },
    checkTableOverflow () {
      const table = this.$refs.table
      if (table) {
        const tableElement = table.$el.querySelector('table')
        if (tableElement) {
          const containerWidth = table.$el.querySelector('.q-table__middle').clientWidth
          this.hasHorizontalOverflow = tableElement.scrollWidth > containerWidth
        }
      }
    },
    toggleFullScreen () {
      this.isFullScreen = !this.isFullScreen
    },
    toggleSearch () {
      if (this.searchQuery) return // Don't allow toggling if parent search is active
      this.showSearchField = !this.showSearchField
      if (!this.showSearchField) {
        this.internalSearchQuery = ''
      } else {
        this.$nextTick(() => {
          this.$refs.searchInput.focus()
        })
      }
    },
    clearSearch () {
      if (this.searchQuery) return // Don't allow clearing if parent search is active
      this.internalSearchQuery = ''
      this.showSearchField = false
      this.$emit('update:searchQuery', '')
    },
    toggleColumnExpansion (field) {
      this.expandedColumns[field] = !this.expandedColumns[field]
    },
    isDecimalComponent (component) {
      const decimalComponents = ['decimal-1', 'decimal-2', 'decimal-4', 'decimal-e1', 'decimal-e2']
      return _.includes(decimalComponents, component)
    },
    getDecimalFormat (colComponent, valueComponent) {
      const component = colComponent || valueComponent

      if (component === 'decimal-e1') return 'decimalExactly1'
      if (component === 'decimal-e2') return 'decimalExactly2'
      if (component === 'decimal-2') return 'decimal2'
      if (component === 'decimal-1') return 'decimal1'
      if (component === 'decimal-4') return 'decimal4'

      return 'decimal1'
    }
  }
}
</script>

<style lang="scss" scoped>
.model-chip:hover {
  outline: 2px solid #333;
}

.share-value-chip {
  width: 50px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
  padding-left: 0px;
  line-height: 1;
}

.q-data-table td, .q-data-table th {
  /* don't shorten cell contents */
  white-space: normal !important;
}

.grand-total {
  border-top: double 2px;
}

.last-of-group {
  border-bottom: solid 1px #999 !important;
}

.new-groupable {
  border-left: solid 1px #999;
  border-bottom: none;

}

.new-groupable:not(.last-group) {
  border-right: none;
}

.not-new-group {
  border-left: solid 1px #999;
  border-bottom: none;
  border-right: none;
}

.group-header-row {
  border-bottom: solid 1px #999 !important;
}

.subgroup {
  border-bottom: none !important;
}

.sub-subgroup {
  border-bottom: solid 1px #999 !important;
  border-right: none;
}

.super-subgroup {
  border-top: none;
  border-right: none;
  border-bottom: none;
  border-left: solid 1px #999;
}

.report-table .search-input {
  margin-right: 8px;
  :deep(.q-field__control) {
    min-height: 20px;
    padding: 0;
  }
  :deep(.q-field__native) {
    padding: 0;
    font-size: 0.8125rem;
    line-height: 1;
  }
  :deep(.q-field__append) {
    padding: 0;
    min-height: 20px;
  }
  .q-field--dense .q-field__control, .q-field--dense .q-field__marginal {
    height: 30px;
  }
}

.subtotal-value {
  border-bottom: solid 1px #999 !important;
}

</style>

<style lang="scss">
/* Styles for sticky headers */
.embedded .report-table:not(.printing) {
    .q-table__container {
      position: relative;
      overflow: visible !important;
      background: rgb(238, 238, 238);
    }
    /* Default positions - no banner */
    .q-table__top {
      position: sticky;
      top: 56px;
      z-index: 2;
      background: rgb(238, 238, 238);
    }
    /* Make the entire thead sticky only when no horizontal overflow */
    &:not(.has-horizontal-overflow) {
      thead {
        position: sticky;
        top: 84px;
        z-index: 1;
        background: rgb(238, 238, 238);
      }
      .q-table__middle {
        max-height: none !important;
        overflow: visible !important;
      }
    }
    /* With banner positions */
    &.has-header-banner {
      .q-table__top {
        top: 76px;
      }
      &:not(.has-horizontal-overflow) {
        thead {
          top: 104px;
        }
      }
    }
    &.has-header-banner.has-custom-description {
      .q-table__top {
        top: 40px;
      }
      &:not(.has-horizontal-overflow) {
        thead {
          top: 94px;
        }
      }
    }
  }
  /* System report styles - no sticky positioning */
  .system-report:not(.full-screen) .report-table:not(.printing) {
    .q-table__container {
      position: relative;
      overflow: visible !important;
      background: rgb(238, 238, 238);
    }
    .q-table__top {
      position: relative;
      background: rgb(238, 238, 238);
    }
    thead {
      position: relative;
      background: rgb(238, 238, 238);
    }
    .q-table__middle {
      max-height: none !important;
      overflow: visible !important;
    }
  }
  /* Full screen mode */
  .full-screen {
    position: fixed !important;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10000;
    background: white !important;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    > div {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0; /* Important for Firefox */
    }
    .force-flex-grow {
      flex: 1;
      min-height: 0; /* Important for Firefox */
      display: flex;
      flex-direction: column;
    }
    .report-table {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0; /* Important for Firefox */
      .q-table__container {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0; /* Important for Firefox */
        background: rgb(238, 238, 238);
      }
      .q-table__top {
        flex-shrink: 0;
        position: sticky;
        top: 0;
        z-index: 2;
        background: rgb(238, 238, 238);
        padding: 8px 16px;
      }
      thead {
        flex-shrink: 0;
        position: sticky;
        top: 0px;
        z-index: 1;
        background: rgb(238, 238, 238);
      }
      .q-table__middle {
        flex: 1;
        overflow: auto !important;
        min-height: 0; /* Important for Firefox */
        width: 100%;
        table {
          width: auto;
          min-width: 100%;
        }
      }
    }
  }
  .force-flex-column {
    display: flex;
    flex-direction: column;
  }

  .report-table.compact-table {
    thead th {
      padding: 2px 6px !important;
      border-top-width: 1px;
      border-bottom-width: 0px;
      background: #F3F3F3;
    }
    thead th {
      border-right-width: 1px;
    }
    thead th:first-child {
      border-left-width: 1px;
    }
    tbody tr td {
      padding: 1px 6px !important;
      background: white;
      border-right-width: 1px;
    }
    tbody tr td:first-child {
      border-left-width: 1px;
    }
    tbody tr:last-child td {
      border-bottom-width: 1px;
    }
    .q-table__top {
      padding: 0px 3px;
    }
  }

  .report-table.plain-table {
    .q-chip {
      color: black !important;
      background: transparent !important;
    }
  }

  @media print {
    .report-table thead {
      display: table-header-group;
      page-break-inside: auto;
      position: static;  /* Reset sticky positioning for print */
    }
    .report-table.repeat-headers thead {
      display: table-header-group;
      page-break-inside: avoid
    }
    .report-table tbody { page-break-inside: avoid }
    .report-table tr { page-break-inside: avoid }
    .report-table td {
      page-break-inside: avoid;
      position: relative;
    }
  }
</style>
